/**
 * HeightSafetyEstimate · iter303ak-fix26 — Quote Sent · T25 · A20
 * ─────────────────────────────────────────────────────────────
 * Auto-fills perimeter + storeys from roof_details[0], loads rates
 * from /api/app-settings/height-safety-rates, computes itemised
 * estimate per selected type. Editable inline so the operator can
 * override perimeter/storeys before the estimate is pushed.
 *
 * On mount, if roof_details[0].perimeter_length is blank, we PATCH the
 * roof_detail row inline once the operator fills it in — so the
 * value flows forward and is available for the Confirm letter later.
 *
 * Writes:
 *   height_safety_estimate = {
 *     perimeter_m, storeys,
 *     scaffold_total, edge_total, anchor_rope_total,
 *     grand_total, rates_used, computed_at,
 *   }
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Ruler, CheckCircle2, RefreshCw, AlertTriangle } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const nz = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function HeightSafetyEstimate({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;
  const types = new Set(capturedData?.height_safety_types || []);

  const [rates, setRates] = useState(null);
  const [perimeter, setPerimeter] = useState(value?.perimeter_m ?? '');
  const [storeys, setStoreys] = useState(value?.storeys ?? '');
  // Blank = use the flat rate × storeys; a typed amount overrides it.
  const [anchorOverride, setAnchorOverride] = useState(value?.anchor_rope_override ?? '');
  const [roofDetailId, setRoofDetailId] = useState(null);
  const [savingBack, setSavingBack] = useState(false);
  const [autoLoaded, setAutoLoaded] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/app-settings/height-safety-rates');
        setRates(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Failed to load rates');
      }
    })();
  }, []);

  // Auto-fill perimeter + storeys from roof_details[0] if the operator
  // hasn't typed anything yet.
  useEffect(() => {
    if (!appraisalId || autoLoaded) return;
    (async () => {
      try {
        const { data: appr } = await api.get(`/appraisals/${appraisalId}`);
        const rd0 = (appr?.roof_details || [])[0] || null;
        if (rd0) {
          setRoofDetailId(rd0._id || rd0.id || null);
          if ((perimeter === '' || perimeter === null) && rd0.perimeter_length) setPerimeter(String(rd0.perimeter_length));
          if ((storeys === '' || storeys === null) && rd0.roof_stories) setStoreys(String(rd0.roof_stories));
        }
        setAutoLoaded(true);
      } catch { setAutoLoaded(true); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appraisalId]);

  const totals = useMemo(() => {
    const p = parseFloat(perimeter) || 0;
    const s = parseFloat(storeys) || 0;
    const hasOverride = types.has('anchor_rope') && anchorOverride !== '' && !isNaN(parseFloat(anchorOverride));
    const onlyAnchor = types.size === 1 && types.has('anchor_rope');
    if (!rates || !s || (!p && !(onlyAnchor && hasOverride))) return null;
    const scaffold_total = types.has('scaffold') ? Number((rates.scaffold_rate_per_m_per_storey || 0) * p * s) : 0;
    const edge_total     = types.has('edge')     ? Number((rates.edge_rate_per_m_per_storey     || 0) * p * s) : 0;
    const anchor_total   = !types.has('anchor_rope') ? 0
      : hasOverride ? parseFloat(anchorOverride)
      : Number((rates.anchor_rope_rate_flat_per_storey || 0) * s);
    return {
      scaffold_total: Math.round(scaffold_total * 100) / 100,
      edge_total:     Math.round(edge_total * 100) / 100,
      anchor_rope_total: Math.round(anchor_total * 100) / 100,
      grand_total:    Math.round((scaffold_total + edge_total + anchor_total) * 100) / 100,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perimeter, storeys, rates, anchorOverride, capturedData?.height_safety_types?.join(',')]);

  // Push to captured_data.
  useEffect(() => {
    if (!onChange || !totals) return;
    onChange({
      perimeter_m: parseFloat(perimeter) || 0,
      storeys: parseFloat(storeys) || 0,
      ...totals,
      anchor_rope_override: anchorOverride === '' ? null : parseFloat(anchorOverride),
      rates_used: rates,
      computed_at: new Date().toISOString(),
    });
  }, [totals, perimeter, storeys, rates, anchorOverride, onChange]);

  const saveBackToRoofDetail = async () => {
    if (!roofDetailId) return;
    setSavingBack(true);
    try {
      await api.put(`/roof-details/${roofDetailId}`, {
        perimeter_length: parseFloat(perimeter) || 0,
        roof_stories: parseFloat(storeys) || 0,
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save to roof details');
    } finally { setSavingBack(false); }
  };

  const valid = !!totals && totals.grand_total > 0;

  return (
    <div className="space-y-4" data-testid="wf-height-safety-estimate">
      <div className="rounded-xl border border-orange-200 bg-orange-50/60 p-4">
        <div className="flex items-start gap-3">
          <Ruler className="w-5 h-5 mt-0.5 shrink-0 text-orange-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-orange-900">Estimate the H&S Cost</div>
            <div className="text-sm mt-1 text-orange-800">
              {params.prompt || 'Perimeter + storeys auto-filled from Roof Details. Adjust below if wrong — the value can save back to Roof Details so it flows forward.'}
            </div>
          </div>
        </div>
      </div>

      {!rates && !err && <div className="p-3 text-xs text-slate-400 italic">Loading rates…</div>}

      {rates && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Perimeter (m) <span className="text-rose-500">*</span></label>
              <input
                type="number"
                step="0.1"
                value={perimeter}
                onChange={(e) => setPerimeter(e.target.value)}
                placeholder="e.g. 42"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded"
                data-testid="wf-hs-perimeter"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Storeys <span className="text-rose-500">*</span></label>
              <input
                type="number"
                step="1"
                min="1"
                value={storeys}
                onChange={(e) => setStoreys(e.target.value)}
                placeholder="1 or 2"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded"
                data-testid="wf-hs-storeys"
              />
            </div>
          </div>

          {roofDetailId && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={saveBackToRoofDetail}
                disabled={savingBack || !perimeter || !storeys}
                className="px-3 py-1.5 rounded-md text-[11px] font-semibold text-indigo-700 bg-white border border-indigo-300 hover:bg-indigo-50 disabled:opacity-40 inline-flex items-center gap-1.5"
                data-testid="wf-hs-save-to-roof-details"
                title="Save these values back to Roof Details so they persist beyond this workflow"
              >
                <RefreshCw size={11} className={savingBack ? 'animate-spin' : ''} />
                {savingBack ? 'Saving…' : 'Save to Roof Details'}
              </button>
            </div>
          )}

          <div className="p-4 border border-orange-200 bg-white rounded-lg">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Estimate breakdown</div>
            <div className="space-y-1 text-sm">
              {types.has('scaffold') && (
                <div className="flex justify-between text-slate-700" data-testid="wf-hs-breakdown-scaffold">
                  <span>Scaffold — {perimeter || 0}m × {storeys || 0} × {nz(rates.scaffold_rate_per_m_per_storey)}</span>
                  <span className="font-mono font-semibold">{nz(totals?.scaffold_total)}</span>
                </div>
              )}
              {types.has('edge') && (
                <div className="flex justify-between text-slate-700" data-testid="wf-hs-breakdown-edge">
                  <span>Edge Protection — {perimeter || 0}m × {storeys || 0} × {nz(rates.edge_rate_per_m_per_storey)}</span>
                  <span className="font-mono font-semibold">{nz(totals?.edge_total)}</span>
                </div>
              )}
              {types.has('anchor_rope') && (
                <div className="flex justify-between items-center gap-2 text-slate-700" data-testid="wf-hs-breakdown-anchor">
                  <span>
                    Anchor & Rope — {anchorOverride !== '' ? 'custom amount' : `flat ${nz(rates.anchor_rope_rate_flat_per_storey)} × ${storeys || 0}`}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="text-slate-400 text-xs">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={anchorOverride !== '' ? anchorOverride : (totals?.anchor_rope_total ?? '')}
                      onChange={(e) => setAnchorOverride(e.target.value)}
                      className="w-28 px-2 py-1 text-sm font-mono font-semibold text-right border border-slate-300 rounded"
                      data-testid="wf-hs-anchor-amount"
                      title="Type an amount to override the flat rate"
                    />
                    {anchorOverride !== '' && (
                      <button type="button" onClick={() => setAnchorOverride('')} className="text-[10px] text-indigo-600 hover:underline" data-testid="wf-hs-anchor-reset">reset</button>
                    )}
                  </span>
                </div>
              )}
              <div className="flex justify-between border-t border-slate-200 pt-2 mt-2 text-orange-900" data-testid="wf-hs-breakdown-total">
                <span className="text-sm font-semibold">Grand estimate</span>
                <span className="text-base font-mono font-bold">{nz(totals?.grand_total)}</span>
              </div>
            </div>
            {types.size === 0 && (
              <div className="mt-3 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-center gap-1.5">
                <AlertTriangle size={12} /> No safety type(s) selected on the previous step — go back and pick at least one.
              </div>
            )}
          </div>
        </>
      )}

      {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-hs-est-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-hs-est-cancel">Cancel</button>}
          <button
            onClick={onNext}
            disabled={!valid}
            className="px-4 py-1.5 rounded-md text-sm bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-hs-est-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
